import { Box, Group, Stack, Text } from "@mantine/core";
import { Controller, useFormContext } from "react-hook-form";
import { InputDropdown } from "@onengine/component-library";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import CollapsableCard from "@/components/ui/CollapsableCard/CollapsableCard";
import { ScopeRuleRows } from "@/components/features/RolesPermissions/SetScopeModal/ScopeRuleRows";
import type { ScopeRule } from "@/components/features/RolesPermissions/SetScopeModal/scopeRules";
import type { DropDownOption } from "@/types/common.types";
import type { FormValues } from "@/routes/user-management/add-user";
import { ALL_CLIENTS } from "./newUserScope";
import { ALL_CLIENTS_LABEL, ENTITY_ACCESS_HELP, ENTITY_ACCESS_TITLE } from "./newUserCopy";
import styles from "./add-user.module.scss";

const ALL_CLIENTS_OPTION = { value: ALL_CLIENTS, label: ALL_CLIENTS_LABEL };

export interface NewUserEntityAccessStepProps {
  entityAccessOptions: DropDownOption[];
  isLoading?: boolean;
  hasMoreEntityAccess?: boolean;
  isLoadingMoreEntityAccess?: boolean;
  onLoadMoreEntityAccess?: () => void;
  /** Set when the session can only grant one entity: the picker is then not a real choice. */
  scopedEntityId?: string | null;
  companies: UserScopeCompany[];
  rulesByEntity: Record<string, ScopeRule[]>;
  onRulesChange: (tenantId: string, rules: ScopeRule[]) => void;
  showErrors: boolean;
  requiredError: (company: UserScopeCompany, rule: ScopeRule) => string | undefined;
  /** Changes on every blocked Create User, so a card the user had closed opens on its problem. */
  attempt: number;
  blockedTenantIds: ReadonlySet<string>;
}

/**
 * Step 2 of New User. Each entity's card is the Set Scope walk-through's rule rows, so a person's
 * limits read and save the same way whether they are set here or later on the role's Users tab.
 */
export function NewUserEntityAccessStep({
  entityAccessOptions,
  isLoading = false,
  hasMoreEntityAccess = false,
  isLoadingMoreEntityAccess = false,
  onLoadMoreEntityAccess,
  scopedEntityId = null,
  companies,
  rulesByEntity,
  onRulesChange,
  showErrors,
  requiredError,
  attempt,
  blockedTenantIds,
}: NewUserEntityAccessStepProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  return (
    <>
      {!scopedEntityId && (
        <Box className={styles.section}>
          <Group className={styles.sectionHeader} justify="space-between" align="center">
            <Text className={styles.sectionTitle}>{ENTITY_ACCESS_TITLE}</Text>
          </Group>
          <Stack className={styles.sectionContent}>
            <Text className={styles.sectionDescription}>{ENTITY_ACCESS_HELP}</Text>
            <Controller
              name="entityAccess"
              control={control}
              render={({ field }) => (
                <InputDropdown
                  mode="multi"
                  label={ENTITY_ACCESS_TITLE}
                  placeholder="Select.."
                  items={entityAccessOptions}
                  error={errors.entityAccess?.message}
                  loading={isLoading || isLoadingMoreEntityAccess}
                  hasMore={hasMoreEntityAccess}
                  onLoadMore={() => onLoadMoreEntityAccess?.()}
                  searchable
                  value={field.value ?? []}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
        </Box>
      )}

      <Stack className={styles.entityCardsList}>
        {companies.map((company) => {
          const blocked = blockedTenantIds.has(company.tenantId);
          return (
            <CollapsableCard
              key={blocked ? `${company.tenantId}-${attempt}` : company.tenantId}
              title={company.tenantName}
              defaultState
              border
              mb="0"
            >
              <Box className={styles.entityCardContent}>
                <ScopeRuleRows
                  company={company}
                  rules={rulesByEntity[company.tenantId] ?? []}
                  onChange={(rules) => onRulesChange(company.tenantId, rules)}
                  showErrors={showErrors}
                  requiredError={(rule) => requiredError(company, rule)}
                  allClientsOption={ALL_CLIENTS_OPTION}
                />
              </Box>
            </CollapsableCard>
          );
        })}
      </Stack>
    </>
  );
}
